/* eslint-disable no-bitwise */
import { createImageData } from "canvas";
import { rgbaNumberToHexString } from "../utils/colour-conversions.ts";
import {
	renderBrickToPixelChannelRows,
	renderBrickUnitToPixelChannelRows,
} from "./render-bricks.ts";
import type { GenericPosition } from "../model/index.ts";
import type { CanvasWithSourceDimensions } from "./canvas-types.ts";
import type { BuildInput } from "./build-input.ts";
import { normaliseBuildInput } from "./build-input.ts";
import { pixAPicKit } from "../pix-a-pic.ts";

function* renderPictureAsBricks(
	ctx: CanvasImageData,
	buildInput: BuildInput,
	brickSizePixels: number,
	ceilBrickSizePixels: number,
	offset: GenericPosition<number>,
	utilityCtx: Parameters<typeof renderBrickToPixelChannelRows>[0] &
		CanvasWithSourceDimensions,
) {
	if (
		utilityCtx.canvas.width < ceilBrickSizePixels ||
		utilityCtx.canvas.height < ceilBrickSizePixels
	) {
		throw new Error(
			`Cache canvas (${utilityCtx.canvas.width}x${utilityCtx.canvas.height}) too small for brick size ${ceilBrickSizePixels}`,
		);
	}

	const {
		noBrickColour,
		data,
		width: bitmapWidth,
		height: bitmapHeight,
	} = normaliseBuildInput(buildInput);

	// Use Map for faster lookups instead of object
	const brickColourCache = new Map<string, readonly Uint8ClampedArray[]>();

	// Pre-compute dimensions and values used in the loop
	const oversizedImageDataWidth = Math.ceil(
		(bitmapWidth - 1) * brickSizePixels + ceilBrickSizePixels,
	);
	const oversizedImageDataHeight = Math.ceil(
		(bitmapHeight - 1) * brickSizePixels + ceilBrickSizePixels,
	);
	const newData = new Uint8ClampedArray(
		oversizedImageDataWidth * oversizedImageDataHeight * 4,
	);

	// Cache the no brick color for faster comparisons
	const noBrickColourRgba = noBrickColour?.rgba;
	const basePlateColourRgba = pixAPicKit.basePlateColour.rgba;

	// Go backwards because the shadow is to the bottom right, this way shadow doesn't get drawn over
	// Using the same row-by-row yielding structure as the original implementation
	for (let y = bitmapHeight - 1; y >= 0; y--) {
		const yOffset = y * bitmapWidth * 4;
		const yPosBricks = Math.round(y * brickSizePixels);

		for (let x = bitmapWidth - 1; x >= 0; x--) {
			const pixelOffset = yOffset + (x << 2);

			// Inline readUInt32BE for better performance
			const buildPixel =
				data[pixelOffset] * 0x1000000 +
				((data[pixelOffset + 1] << 16) |
					(data[pixelOffset + 2] << 8) |
					data[pixelOffset + 3]);

			const isBasePlate = buildPixel === noBrickColourRgba;
			const pixel = isBasePlate ? basePlateColourRgba : buildPixel;

			// Create a unique key for the color cache
			const cacheKey = `${pixel}:${isBasePlate ? 1 : 0}`;

			let pixelRows = brickColourCache.get(cacheKey);
			if (pixelRows === undefined) {
				const colourString = rgbaNumberToHexString(pixel);
				pixelRows = isBasePlate
					? renderBrickUnitToPixelChannelRows(
							utilityCtx,
							colourString,
							ceilBrickSizePixels,
						)
					: renderBrickToPixelChannelRows(
							utilityCtx,
							colourString,
							ceilBrickSizePixels,
						);

				brickColourCache.set(cacheKey, pixelRows);
			}

			const pixelXI = (x * brickSizePixels) | 0;

			// Copy all pixel rows at once for better performance
			for (let sourceRow = 0; sourceRow < pixelRows.length; sourceRow++) {
				const pixelRow = pixelRows[sourceRow];
				const destRowOffset =
					((yPosBricks + sourceRow) * oversizedImageDataWidth + pixelXI) * 4;

				// Using TypedArray.set for faster copying
				newData.set(pixelRow, destRowOffset);
			}
		}
		// Yield after each row, exactly like the original implementation
		yield;
	}

	// Create the final image data in one operation
	ctx.putImageData(
		createImageData(
			newData,
			oversizedImageDataWidth,
			oversizedImageDataHeight,
		) as ImageData,
		offset.x,
		offset.y,
	);
}

export { renderPictureAsBricks };
